import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Chip
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { format, startOfDay, endOfDay, subDays, subMonths, startOfWeek, endOfWeek } from 'date-fns';
import { timeService } from '../services/timeService';
import { employeeService } from '../services/employeeService';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';
import ReportExportMenu from '../components/ReportExportMenu';

const WEEK_STARTS_SUNDAY = 0;
const OT_THRESHOLD_HOURS = 40;

const toYMD = (d) => (d ? format(d, 'yyyy-MM-dd') : '');

const PRESETS = [
  { label: 'This Week', getRange: () => {
    const now = new Date();
    const from = startOfWeek(now, { weekStartsOn: WEEK_STARTS_SUNDAY });
    const to = endOfWeek(now, { weekStartsOn: WEEK_STARTS_SUNDAY });
    return { from: startOfDay(from), to: endOfDay(to) };
  } },
  { label: 'Last Week', getRange: () => {
    const now = new Date();
    const thisWeekStart = startOfWeek(now, { weekStartsOn: WEEK_STARTS_SUNDAY });
    const lastWeekStart = subDays(thisWeekStart, 7);
    const lastWeekEnd = subDays(thisWeekStart, 1);
    return { from: startOfDay(lastWeekStart), to: endOfDay(lastWeekEnd) };
  } },
  { label: 'Last 2 Weeks', getRange: () => ({ from: startOfDay(subDays(new Date(), 13)), to: endOfDay(new Date()) }) },
  { label: 'This Month', getRange: () => ({ from: startOfDay(subMonths(new Date(), 0)), to: endOfDay(new Date()) }) },
  { label: 'Last Month', getRange: () => ({ from: startOfDay(subMonths(new Date(), 1)), to: endOfDay(subMonths(new Date(), 0)) }) }
];

function groupEntries(entries, granularity) {
  const byKey = new Map();

  for (const e of entries) {
    if (!e.clock_in) continue;
    const d = new Date(e.clock_in);
    if (isNaN(d.getTime())) continue;
    const baseDate = startOfDay(d);
    let key;
    let label;

    if (granularity === 'day') {
      key = `${format(baseDate, 'yyyy-MM-dd')}::${e.employee_id}`;
      label = format(baseDate, 'MM/dd/yyyy');
    } else {
      const weekStart = startOfWeek(baseDate, { weekStartsOn: WEEK_STARTS_SUNDAY });
      const weekEnd = endOfWeek(baseDate, { weekStartsOn: WEEK_STARTS_SUNDAY });
      key = `${format(weekStart, 'yyyy-MM-dd')}::${e.employee_id}`;
      label = `${format(weekStart, 'MM/dd')} – ${format(weekEnd, 'MM/dd')}`;
    }

    if (!byKey.has(key)) {
      byKey.set(key, {
        employeeId: e.employee_id,
        employeeNumber: e.employee_number,
        employeeName: [e.employee_first_name, e.employee_last_name].filter(Boolean).join(' ') || '—',
        periodLabel: label,
        totalHours: 0
      });
    }
    const rec = byKey.get(key);
    rec.totalHours += parseFloat(e.hours_worked) || 0;
  }

  const rows = Array.from(byKey.values());
  rows.forEach((r) => {
    const total = r.totalHours;
    r.regularHours = Math.min(OT_THRESHOLD_HOURS, total);
    r.overtimeHours = Math.max(0, total - OT_THRESHOLD_HOURS);
  });

  return rows.sort((a, b) => {
    if (a.employeeName === b.employeeName) {
      return a.periodLabel.localeCompare(b.periodLabel);
    }
    return a.employeeName.localeCompare(b.employeeName);
  });
}

const PayrollReport = () => {
  const DEFAULT_COL_WIDTHS = {
    employee: 220,
    employeeNo: 120,
    period: 180,
    regular: 120,
    overtime: 120,
    total: 120
  };
  const [dateFrom, setDateFrom] = useState(() => toYMD(startOfDay(subDays(new Date(), 13))));
  const [dateTo, setDateTo] = useState(() => toYMD(endOfDay(new Date())));
  const [granularity, setGranularity] = useState('week'); // 'day' or 'week'
  const [employeeId, setEmployeeId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [employeeSearchInput, setEmployeeSearchInput] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [entries, setEntries] = useState([]);
  const [sortKey, setSortKey] = useState('employeeName');
  const [sortDirection, setSortDirection] = useState('asc');
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_payroll_hours_col_widths',
    minWidths: { employee: 140, employeeNo: 100, period: 130, regular: 100, overtime: 100, total: 100 }
  });

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setEmployeesLoading(true);
        const res = await employeeService.getAll({ activeOnly: 'true', limit: 500 });
        setEmployees(res.data ?? []);
      } catch (e) {
        console.error('Error loading employees:', e);
        setEmployees([]);
      } finally {
        setEmployeesLoading(false);
      }
    };
    loadEmployees();
  }, []);

  const handlePreset = (preset) => {
    const { from, to } = preset.getRange();
    setDateFrom(toYMD(from));
    setDateTo(toYMD(to));
  };

  const loadReport = async () => {
    if (!dateFrom || !dateTo) {
      setError('Select date range (From and To).');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const params = {
        dateFrom,
        dateTo,
        limit: 5000,
        ...(employeeId ? { employeeId } : {})
      };
      const res = await timeService.getAll(params);
      const list = Array.isArray(res.data) ? res.data : res.data ?? [];
      setEntries(list);
    } catch (err) {
      console.error('Error loading payroll report entries:', err);
      setEntries([]);
      setError(err.response?.data?.message || 'Failed to load payroll report');
    } finally {
      setLoading(false);
    }
  };

  const groupedRows = useMemo(() => groupEntries(entries, granularity), [entries, granularity]);

  const totalHours = useMemo(
    () => groupedRows.reduce((sum, r) => sum + (Number(r.totalHours) || 0), 0),
    [groupedRows]
  );
  const totalRegularHours = useMemo(
    () => groupedRows.reduce((sum, r) => sum + (Number(r.regularHours) ?? 0), 0),
    [groupedRows]
  );
  const totalOvertimeHours = useMemo(
    () => groupedRows.reduce((sum, r) => sum + (Number(r.overtimeHours) ?? 0), 0),
    [groupedRows]
  );

  const selectedEmployee = useMemo(() => {
    if (!employeeId) return null;
    return employees.find((e) => String(e.id) === String(employeeId)) || null;
  }, [employeeId, employees]);

  const handleSort = (key) => {
    setSortDirection((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const displayedRows = useMemo(() => {
    const list = [...groupedRows];
    const getter = (row) => {
      switch (sortKey) {
        case 'employeeName': return row.employeeName ?? '';
        case 'employeeNumber': return row.employeeNumber ?? '';
        case 'periodLabel': return row.periodLabel ?? '';
        case 'regularHours': return Number(row.regularHours) || 0;
        case 'overtimeHours': return Number(row.overtimeHours) || 0;
        case 'totalHours': return Number(row.totalHours) || 0;
        default: return '';
      }
    };
    const dir = sortDirection === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }, [groupedRows, sortKey, sortDirection]);

  const getPayrollExportData = useCallback(() => {
    if (!displayedRows.length) return null;
    const headers = ['Employee', 'Emp #', 'Period', 'Regular hrs', 'OT hrs', 'Total hrs'];
    const rows = displayedRows.map((row) => [
      row.employeeName,
      row.employeeNumber != null && row.employeeNumber !== '' ? String(row.employeeNumber) : '',
      row.periodLabel,
      row.regularHours.toFixed(2),
      row.overtimeHours > 0 ? row.overtimeHours.toFixed(2) : '—',
      row.totalHours.toFixed(2)
    ]);
    const footer = [
      '',
      '',
      'Totals',
      totalRegularHours.toFixed(2),
      totalOvertimeHours.toFixed(2),
      totalHours.toFixed(2)
    ];
    const granLabel = granularity === 'week' ? 'Weekly (Sun–Sat)' : 'Daily';
    const emp =
      selectedEmployee != null
        ? `${selectedEmployee.employee_number || ''} ${[selectedEmployee.first_name, selectedEmployee.last_name].filter(Boolean).join(' ')}`.trim()
        : 'All employees';
    const pdfTitle = `Payroll hours · ${dateFrom} – ${dateTo} · ${granLabel} · ${emp}`;
    const filenameBase = `payroll-hours_${dateFrom}_${dateTo}_${granularity}`;
    return { headers, rows, footer, pdfTitle, filenameBase, pdfLandscape: false };
  }, [
    displayedRows,
    totalRegularHours,
    totalOvertimeHours,
    totalHours,
    granularity,
    selectedEmployee,
    dateFrom,
    dateTo
  ]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Payroll Hours Report
        </Typography>
      </Box>

      {error && (
        <Paper sx={{ p: 1.5, mb: 1.5, border: '1px solid #f44336', backgroundColor: '#ffebee' }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Filters
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">Date presets:</Typography>
            {PRESETS.map((p) => (
              <Chip
                key={p.label}
                label={p.label}
                size="small"
                onClick={() => handlePreset(p)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
          <TextField
            size="small"
            label="Date From"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            size="small"
            label="Date To"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <FormControl size="small" sx={{ width: 180 }}>
            <InputLabel>Breakdown</InputLabel>
            <Select
              label="Breakdown"
              value={granularity}
              onChange={(e) => setGranularity(e.target.value)}
            >
              <MenuItem value="day">Per day</MenuItem>
              <MenuItem value="week">Per week (Sun–Sat)</MenuItem>
            </Select>
          </FormControl>
          <Autocomplete
            size="small"
            sx={{ minWidth: 260 }}
            loading={employeesLoading}
            options={employees}
            getOptionLabel={(emp) => (emp ? `${emp.employee_number || ''} ${[emp.first_name, emp.last_name].filter(Boolean).join(' ')}`.trim() : '')}
            inputValue={employeeSearchInput}
            onInputChange={(_, value) => setEmployeeSearchInput(value)}
            value={selectedEmployee ?? null}
            onChange={(_, newVal) => {
              setEmployeeId(newVal ? String(newVal.id) : '');
              if (!newVal) setEmployeeSearchInput('');
            }}
            isOptionEqualToValue={(opt, val) => opt && val && String(opt.id) === String(val.id)}
            renderInput={(params) => (
              <TextField {...params} label="Employee (optional)" placeholder="All employees if left blank" />
            )}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Search />}
            onClick={loadReport}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Run report'}
          </Button>
          <ReportExportMenu
            disabled={loading || displayedRows.length === 0}
            getData={getPayrollExportData}
            defaultFilenameBase="payroll-hours"
          />
        </Box>
        {selectedEmployee && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Selected employee: {selectedEmployee.employee_number} — {[selectedEmployee.first_name, selectedEmployee.last_name].filter(Boolean).join(' ')}
            </Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Results
          </Typography>
          {dateFrom && dateTo && (
            <Typography variant="caption" color="text.secondary">
              {dateFrom} — {dateTo}
              {granularity === 'week' ? ' · Weekly totals (Sun–Sat)' : ' · Daily totals'}
            </Typography>
          )}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <ResizableHeaderCell width={colWidths.employee} onResizeStart={(e) => startResize('employee', e)} sortable sortActive={sortKey === 'employeeName'} sortDirection={sortDirection} onSort={() => handleSort('employeeName')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Employee</Typography></ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.employeeNo} onResizeStart={(e) => startResize('employeeNo', e)} sortable sortActive={sortKey === 'employeeNumber'} sortDirection={sortDirection} onSort={() => handleSort('employeeNumber')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Emp #</Typography></ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.period} onResizeStart={(e) => startResize('period', e)} sortable sortActive={sortKey === 'periodLabel'} sortDirection={sortDirection} onSort={() => handleSort('periodLabel')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Period</Typography></ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.regular} onResizeStart={(e) => startResize('regular', e)} align="right" sortable sortActive={sortKey === 'regularHours'} sortDirection={sortDirection} onSort={() => handleSort('regularHours')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Regular hrs</Typography></ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.overtime} onResizeStart={(e) => startResize('overtime', e)} align="right" sortable sortActive={sortKey === 'overtimeHours'} sortDirection={sortDirection} onSort={() => handleSort('overtimeHours')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>OT hrs</Typography></ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.total} onResizeStart={(e) => startResize('total', e)} align="right" sortable sortActive={sortKey === 'totalHours'} sortDirection={sortDirection} onSort={() => handleSort('totalHours')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Total hrs</Typography></ResizableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : displayedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No data. Adjust filters and run the report.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedRows.map((row, idx) => (
                  <TableRow key={`${row.employeeId}-${row.periodLabel}-${idx}`}>
                    <TableCell sx={{ width: colWidths.employee }}>{row.employeeName}</TableCell>
                    <TableCell sx={{ width: colWidths.employeeNo }}>{row.employeeNumber || '—'}</TableCell>
                    <TableCell sx={{ width: colWidths.period }}>{row.periodLabel}</TableCell>
                    <TableCell sx={{ width: colWidths.regular }} align="right">{row.regularHours.toFixed(2)}</TableCell>
                    <TableCell sx={{ width: colWidths.overtime }} align="right">{row.overtimeHours > 0 ? row.overtimeHours.toFixed(2) : '—'}</TableCell>
                    <TableCell sx={{ width: colWidths.total }} align="right">{row.totalHours.toFixed(2)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {groupedRows.length > 0 && (
          <Box sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Regular: {totalRegularHours.toFixed(2)} hrs
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              OT: {totalOvertimeHours.toFixed(2)} hrs
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Grand total: {totalHours.toFixed(2)} hrs
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PayrollReport;

